import { useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import * as yup from "yup";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import Loader from "Components/Base/BaseLoader";
import { toast } from "react-toastify";
import {
  checkStatusCodeSuccess,
  NormalDate,
  notFound,
  TaxType,
} from "Components/constants/Common";
import {
  InputPlaceHolder,
  SelectPlaceHolder,
  validationMessages,
} from "../../Components/constants/validation";
import { BaseSelect } from "Components/Base/BaseSelect";
import { listOfProductForDropdown } from "Api/common";
import { cancelLabel, submitLabel } from "Components/constants/Role";
import {
  AddProductLabel,
  OrderDateLabel,
  TotalCostLabel,
} from "Components/constants/PurchaseOrder";
import { createOrder, orderRefund, viewOrderApi } from "Api/Order";
import {
  ADD_ORDER_LABEL,
  EXPECTED_SHIPMENT_DATE,
  EXPECTED_SHIPMENT_DATE_ERROR,
  REASON_LABEL,
  RETURN_DATE,
  RETURN_LABEL,
  RETURN_ORDER_LABEL,
  RETURN_QUANTITY,
  RETURN_QUANTITY_ERROR,
  ReturnOrderReasons,
  SHIPMENT_DATE_LABEL,
  TOTAL_PRICE_KEY,
} from "Components/constants/Order";
import {
  CustomerIdLabel,
  CustomerLabel,
  CustomerNameLabel,
} from "Components/constants/Staff";
import { listOfCustomer } from "Api/customer";
import {
  ACTION_LABEL,
  ALL_FIELDS_ARE_REQUIRED,
  AMOUNT_LABEL,
  GST_LABEL,
  ITEM_CODE_LABEL,
  MAKE_KEY,
  MAKE_LABEL,
  PLEASE_ENTER_PROD_DETAILS_FOR_NEW_ROW,
  PRODUCT_DELETE_SUCCESS,
  QTY_GREATER_THAN_ZERO,
  QTY_LABEL,
  RATE_LABEL,
  REQ_QTY_CANNOT_EXCEED_PENDING_QTY,
  SELECT_ITEM_CODE,
  SR_NO_LABEL,
  STOCK_LABEL,
} from "Components/constants/ROModule";
import { listOfItemCode, listOfProductForItem } from "Api/PartRequisition";
import { listOfManufacturer } from "Api/ManuFacturer";
import { listOfRate, listOfStock } from "Api/RoPurchaseOrder";
import { amountCountApi } from "Api/Quotation";
import { AgGridReact } from "ag-grid-react";
import DeleteModal from "Components/Base/DeleteModal";
import { Column } from "ag-grid-community";
import Select from "react-select";

const validationSchema = yup.object({
  auth_draft_id: yup
    .string()
    .required(validationMessages.required(CustomerNameLabel)),
  order_date: yup
    .string()
    .required(validationMessages.required(OrderDateLabel)),
  expected_shipment_date: yup
    .string()
    .required(validationMessages.required(EXPECTED_SHIPMENT_DATE))
    .test(
      "shipment-date-after-order",
      EXPECTED_SHIPMENT_DATE_ERROR,
      function (value) {
        const { order_date } = this.parent;
        return !order_date || new Date(value) >= new Date(order_date);
      }
    ),
});

const validationReturnSchema = yup.object({
  return_date: yup.string().required(validationMessages.required(RETURN_DATE)),
  reason: yup.string().required(validationMessages.required(REASON_LABEL)),
  orderItem: yup.array().of(
    yup.object().shape({
      return_quantity: yup
        .number()
        .required(validationMessages.required(RETURN_QUANTITY))
        .test(
          "return-quantity-less-than-quantity",
          RETURN_QUANTITY_ERROR,
          function (value) {
            const { quantity } = this.parent;
            return value <= quantity;
          }
        ),
    })
  ),
});
interface OrderRow {
  item_code?: string | null;
  make?: string | null;
  rate?: number | null;
  required_quantity?: number | null;
  gst?: string | null;
  stock?: any;
  amount?: number | null;
}

const validateRequiredFields = (viewPR: OrderRow[]): boolean => {
  if (!viewPR || viewPR.length === 0) {
    return false;
  }
  return viewPR.every(
    (row) =>
      row.item_code?.trim() &&
      row.rate &&
      row.rate > 0 &&
      row.required_quantity &&
      row.required_quantity > 0 &&
      row.gst?.trim()
  );
};

const validateStockAvailability = (viewPR: OrderRow[]): boolean => {
  return viewPR.every((row) => {
    const stock = Number(row.stock || 0);
    const requestedQty = Number(row.required_quantity || 0);
    return requestedQty <= stock;
  });
};

const validateQuantities = (viewPR: OrderRow[]): boolean => {
  return viewPR.every((row) => {
    const qty = Number(row.required_quantity || 0);
    return qty > 0;
  });
};

const createEmptyRow = (): any => ({
  tempId: `temp-${Date.now()}-${Math.random()}`,
  id: null,
  item_code: "",
  product_id: "",
  make: "",
  manufacturer_id: "",
  product_variant_id: "",
  required_quantity: "",
  rate: "",
  gst: "",
  amount: "",
  tax: "",
  return_quantity: "",
  final_amount: "",
  stock: "",
  srNo: "",
  parts_name: "",
  uom: "",
  qty: "",
  store_location: "",
  project_name: "",
  rack_location: "",
  remarks: "",
  material_type: "",
  material_group: "",
  material_group_description: "",
});
const ensure11Rows = (data: any[] = []): any[] => {
  const rows = [...data];
  const emptyRowsNeeded = Math.max(0, 11 - rows.length);
  for (let i = 0; i < emptyRowsNeeded; i++) {
    rows.push(createEmptyRow());
  }
  return rows;
};

const syncGridDataWithForm = (gridData: any[], formik: any) => {
  const orderItems = gridData.map((row) => ({
    product_id: row.product_id || null,
    product_variant_id: row.product_variant_id || null,
    manufacturer_id: row.manufacturer_id || null,
    quantity: Number(row.required_quantity) || 0,
    tax: row.gst || "",
    rate: Number(row.rate) || 0,
    return_quantity: row.required_quantity || 0,
    final_amount: row.amount || 0,
  }));

  formik.setFieldValue("orderItem", orderItems);
};

interface Order {
  product_id: number | null;
  availableQuantity: number | null;
  quantity: number | null;
  rate: number | null;
  tax: string;
  return_quantity?: null;
  final_amount: number | null;
  refund_amount?: number | null;
  product_variant_id: any;
  manufacturer_id: any;
}
interface OrderInitialValues {
  order_type: string;
  auth_id: number | null;
  auth_draft_id: string;
  project_id: number | null;
  order_date: string;
  expected_shipment_date: string;
  discount_value: number | null;
  total_price: number | null;
  reason?: string;
  return_date?: string;
  orderItem: Order[];
}

interface CustomerOption {
  value: string;
  label: string;
  auth_id: string;
}
const ItemCodeEditor = ({ params }: { params: any }) => {
  const itemCodes = params.itemCodes || [];

  const formattedItemCodes = itemCodes.map((item: any) => {
    if (typeof item === "object" && item.value && item.label) {
      return item;
    }

    if (typeof item === "object" && item.value) {
      return {
        value: String(item.value),
        label: String(item.value),
      };
    }

    return {
      value: String(item),
      label: String(item),
    };
  });

  const handleChange = (selectedOption: any) => {
    params.stopEditing();

    if (!selectedOption) {
      const resetData = {
        ...params.data,
        tempId: params.data.tempId,
        srNo: params.data.srNo,
        item_code: "",
        parts_name: "",
        uom: "",
        stock: "",
        rate: "",
        make: "",
        manufacturer_id: null,
        product_variant_id: null,
        product_id: null,
        material_type: "",
        material_group: "",
        material_group_description: "",
        store_location: "",
        rack_location: "",
        qty: "",
        project_name: "",
        remarks: "",
      };

      if (params.context.updateRowData) {
        params.context.updateRowData(params.rowIndex, resetData);
      }

      params.api.applyTransaction({
        update: [resetData],
      });

      return;
    }

    const optionValue = selectedOption?.value ?? "";
    let label = selectedOption?.label ?? "";
    let id = optionValue;

    if (typeof optionValue === "string" && optionValue.includes("|")) {
      const parts = optionValue.split("|");
      label = parts[0];
      id = parts[1] || label;
    }

    params.node.setDataValue("item_code", label);
    params.context.handleProductSelect(label, params.rowIndex, Number(id));
  };

  const selectedItemCode = formattedItemCodes.find(
    (item: { value: string; label: string }) => item.label === params.value
  );

  return (
    <Select
      name="item_code"
      options={formattedItemCodes}
      value={selectedItemCode || null}
      onChange={handleChange}
      placeholder={SELECT_ITEM_CODE}
      menuPosition="fixed"
      menuPlacement="auto"
      isClearable={true}
      styles={{
        control: (provided: any) => ({
          ...provided,
          width: "180px",
        }),
        menu: (provided: any) => ({
          ...provided,
          width: "180px",
        }),
      }}
    />
  );
};
const AddOrder = () => {
  const { id } = useParams();
  document.title = id ? RETURN_ORDER_LABEL : ADD_ORDER_LABEL;
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [btnLoader, setBtnLoader] = useState(false);
  const [productId, setProductId] = useState<number[]>([]);
  const [initialTotalCost, setInitialTotalCost] = useState<number>();
  const [customerList, setCustomerList] = useState<CustomerOption[]>([]);
  const [viewOrderData, setViewOrderData] = useState<any>();
  const [products, setProducts] = useState([]);
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [customPageSize, setCustomPageSize] = useState(1000);
  const [itemCodes, setItemCodes] = useState<any[]>([]);
  const [makeOption, setMakeOption] = useState<Array<any>>([]);
  const [rateOptions, setRateOptions] = useState<Array<any>>([]);
  const gridRef = useRef<AgGridReact<any>>(null);
  const [viewPR, setViewPR] = useState<any[]>([]);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [deleteRowData, setDeleteRowData] = useState<any>(null);
  const location = useLocation();
  const isReturnRoute = location.pathname.includes("return-order");

  const handleCellKeyDown = (event: any) => {
    if (event.event.key === "Enter") {
      const { node, column } = event;
      const gridApi = gridRef.current?.api;
      const columnDefs = gridApi?.getColumns() || [];
      const lastColumnIndex = columnDefs.length - 1;

      if (column === columnDefs[lastColumnIndex]) {
        if (node.rowIndex === viewPR.length - 1) {
          handleAddProduct();
          setTimeout(() => {
            const firstEditableColumn = columnDefs.find(
              (col: Column) => col.getColDef().editable
            );
            if (firstEditableColumn) {
              gridApi?.startEditingCell({
                rowIndex: node.rowIndex + 1,
                colKey: firstEditableColumn.getColId(),
              });
            }
          }, 100);
        }
      }
    }
  };

  const fetchCustomer = () => {
    setLoader(true);
    const payload = {
      sortKey: columnName,
      sortValue: sortOrder,
      page: currentPage,
      type_of_user: "Customer",
      limit: customPageSize,
    };
    listOfCustomer(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setCustomerList(
            resp?.data?.positions?.map((item: any) => ({
              value: item?.id,
              label: item?.name,
              auth_id: item?.user_draft_id,
            }))
          );
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const handleCustomerChange = (selectedCustomerId: string | null) => {
    if (selectedCustomerId) {
      const selectedCustomer = customerList.find(
        (item) => item.value === selectedCustomerId
      );

      if (selectedCustomer) {
        formik.setFieldValue("auth_id", selectedCustomer.auth_id);
      } else {
        formik.setFieldValue("auth_id", "");
      }
    } else {
      formik.setFieldValue("auth_id", "");
    }
  };
  const fetchProduct = () => {
    setLoader(true);
    let data;
    listOfProductForDropdown()
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          data = resp?.data;
          setProducts(
            data?.map((item: any) => ({
              value: item?.id,
              label: `${item?.product_code} (${item.productVariant?.[0]?.batch_no})`,
              rate: item?.rate,
              quantity: item?.quantity,
              id: item?.id,
            }))
          );
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const fetchOrder = () => {
    setLoader(true);
    viewOrderApi(Number(id))
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setViewOrderData(resp?.data);
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const initialValues: OrderInitialValues = {
    order_type: "",
    auth_id: null,
    auth_draft_id: "",
    project_id: null,
    order_date: NormalDate(String(new Date())),
    expected_shipment_date: "",
    discount_value: 0,
    total_price: 0,
    reason: "",
    return_date: NormalDate(String(new Date())),
    orderItem: [
      {
        product_id: null,
        availableQuantity: null,
        quantity: null,
        rate: null,
        tax: "",
        return_quantity: null,
        final_amount: null,
        refund_amount: null,
        manufacturer_id: null,
        product_variant_id: null,
      },
    ],
  };
  const formik = useFormik<OrderInitialValues>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: id ? validationReturnSchema : validationSchema,
    onSubmit: (values) => {
      const filledRows = viewPR.filter(
        (row) => row.product_id || row.item_code
      );

      if (filledRows.length === 0) {
        toast.error(PLEASE_ENTER_PROD_DETAILS_FOR_NEW_ROW);
        return;
      }

      const isValid = validateRequiredFields(filledRows);
      if (!isValid) {
        toast.error(ALL_FIELDS_ARE_REQUIRED);
        return;
      }

      const hasValidStock = validateStockAvailability(viewPR);
      if (!hasValidStock) {
        toast.error(REQ_QTY_CANNOT_EXCEED_PENDING_QTY);
        return;
      }

      const hasValidQuantities = validateQuantities(filledRows);
      if (!hasValidQuantities) {
        toast.error(QTY_GREATER_THAN_ZERO);
        return;
      }

      if (id) {
        let filteredOrderDetail: any = values?.orderItem?.filter(
          (item: Order) => {
            return Number(item.return_quantity) > 0;
          }
        );
        const payload = {
          order_id: Number(id),
          reason: values.reason,
          return_date: values.return_date,
          orderDetail: filteredOrderDetail.map((item: any) => {
            return {
              product_id: item.product_id,
              product_variant_id: item.product_variant_id,
              received_quantity: Number(item.quantity),
              tax: item.tax,
              rate: Number(item.rate),
              return_quantity: Number(item.return_quantity),
              amount: item.final_amount,
              refund_amount: Number(item.refund_amount),
            };
          }),
        };
        setBtnLoader(true);
        orderRefund(payload)
          .then((resp) => {
            if (checkStatusCodeSuccess(resp?.statusCode)) {
              toast.success(resp?.message);
              navigate("/order");
            } else {
              toast.error(resp?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
            return err;
          })
          .finally(() => {
            setBtnLoader(false);
          });
      } else {
        const payload = {
          order_type: values.order_type || CustomerLabel,
          auth_id: values.auth_draft_id,
          project_id: values.project_id || null,
          order_date: values.order_date,
          expected_shipment_date: values.expected_shipment_date,
          discount_value: values.discount_value,
          total_price: values.total_price,
          orderItem: values.orderItem
            .filter((item: Order) => item.product_id)
            .map((item: Order) => ({
              product_id: item.product_id,
              product_variant_id: item.product_variant_id,
              manufacturer_id: item.manufacturer_id,
              quantity: Number(item.quantity),
              tax: item.tax,
              rate: Number(item.rate),
              return_quantity: Number(item.return_quantity),
              final_amount: item.final_amount,
            })),
        };
        setBtnLoader(true);
        createOrder(payload)
          .then((resp) => {
            if (checkStatusCodeSuccess(resp?.statusCode)) {
              toast.success(resp?.message);
              navigate("/order");
            } else {
              toast.error(resp?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
            return err;
          })
          .finally(() => {
            setBtnLoader(false);
          });
      }
    },
  });

  useEffect(() => {
    listOfItemCode({})
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const data = res?.data?.product;
          const formattedItemCodes = data.map((item: any) => ({
            value: item.id,
            label: item.product_code,
          }));
          setItemCodes(formattedItemCodes);
        } else {
          toast.error(res.message);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      });
  }, []);

  const handleProductSelect = async (
    productCode: string,
    rowIndex: number,
    selectedId: number
  ) => {
    try {
      const response = await listOfProductForItem({
        product_id: selectedId,
      });

      if (response?.statusCode === 200 && response.data?.[0]) {
        const productDetails = response.data[0];
        const productVariant = productDetails.productVariant?.[0];

        setViewPR((prevData) => {
          const updatedData = [...prevData];
          const existingRow = updatedData[rowIndex] || {};

          const updatedRow = {
            ...existingRow,
            tempId: existingRow.tempId || `temp-${Date.now()}-${Math.random()}`,
            id: existingRow?.id || null,
            item_code: productDetails.product_code || "",
            stock: productVariant?.quantity ?? "",
            product_id: selectedId,
            manufacturer_id: productVariant?.manufacturer_id ?? "",
            product_variant_id: productVariant?.id ?? "",
            rate: productVariant?.rate ?? "",
            make: productVariant?.manufacturer?.name ?? "",
            required_quantity: 1,
            gst: TaxType[0]?.value ?? "",
            amount: productVariant?.rate ?? 0,
            tax: TaxType[0]?.value ?? "",
            return_quantity: "",
            final_amount: productVariant?.rate ?? 0,
          };

          updatedData[rowIndex] = updatedRow;

          countAmount(updatedRow, rowIndex);
          syncGridDataWithForm(updatedData, formik);

          if (productDetails.id && productVariant?.manufacturer_id) {
            fetchRate(productDetails.id, productVariant.manufacturer_id);
          }

          return ensure11Rows(updatedData);
        });
      } else {
        toast.error(response?.message);
      }
    } catch (err: any) {
      toast.error(err.message);
    }
  };
  const fetchMake = async () => {
    setLoader(true);
    try {
      const res = await listOfManufacturer({});
      if (checkStatusCodeSuccess(res?.statusCode)) {
        const data = res?.data?.manufacturers?.map((item: any) => ({
          value: item?.id,
          label: item?.name,
          id: item?.id,
        }));
        setMakeOption(data);
      } else {
        toast.error(res.message);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchMake();
  }, []);

  const fetchRate = async (productId: number, manufacturerId: number) => {
    setLoader(true);
    try {
      const payload = {
        product_id: productId,
        makeId: manufacturerId,
      };

      const res = await listOfRate(payload);
      if (checkStatusCodeSuccess(res?.statusCode)) {
        const data = res?.data?.map((item: any) => ({
          value: String(item?.rate),
          label: String(item?.rate),
          id: item?.id,
        }));

        setRateOptions((prev) => ({
          ...prev,
          [productId]: data,
        }));
      } else {
        toast.error(res.message);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      setLoader(false);
    }
  };

  const countAmount = (data: any, rowIndex: number) => {
    const payload = {
      quantity: data.required_quantity,
      gst: data.gst,
      rate: Number(data.rate),
    };

    const isValidPayload =
      payload.quantity !== null &&
      payload.gst !== null &&
      payload.rate !== null;

    if (isValidPayload) {
      setLoader(true);

      amountCountApi(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            const updatedAmount = res?.data;

            setViewPR((prevData) => {
              const updatedData = [...prevData];
              updatedData[rowIndex] = {
                ...updatedData[rowIndex],
                amount: updatedAmount,
              };
              syncGridDataWithForm(updatedData, formik);
              return updatedData;
            });
            if (gridRef.current?.api) {
              const rowNode =
                gridRef.current.api.getDisplayedRowAtIndex(rowIndex);
              if (rowNode) {
                gridRef.current.api.refreshCells({
                  rowNodes: [rowNode],
                  columns: ["amount"],
                });
              }
            }
          }
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || err?.message);
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  useEffect(() => {
    syncGridDataWithForm(viewPR, formik);
  }, [viewPR]);

  useEffect(() => {
    const newTotal = viewPR.reduce((acc, row) => acc + (row.amount || 0), 0);
    setTotalAmount(newTotal);
  }, [viewPR]);

  const handleGSTSelection = (newValue: string, rowIndex: number) => {
    if (rowIndex === 0) {
      const selectedGST = TaxType.find((tax) => tax.label === newValue);
      if (selectedGST) {
        setViewPR((prevData) => {
          const updatedData = prevData.map((row) => {
            const updatedRow = {
              ...row,
              gst: selectedGST.value,
            };
            if (updatedRow.required_quantity && updatedRow.rate) {
              countAmount(updatedRow, prevData.indexOf(row));
            }
            return updatedRow;
          });
          return updatedData;
        });
      }
    }
  };

  const handleAddProduct = () => {
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const newRow = {
      tempId,
      srNo: "",
      item_code: "",
      parts_name: "",
      uom: "",
      qty: "",
      stock: "",
      store_location: "",
      project_name: "",
      rack_location: "",
      remarks: "",
      material_type: "",
      material_group: "",
      material_group_description: "",
    };
    setViewPR((prevData) => {
      return ensure11Rows([...prevData, createEmptyRow()]);
    });
  };

  const handleDeleteProduct = (rowData: any) => {
    setDeleteRowData(rowData);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
    setDeleteRowData(null);
  };

  const handleDeleteConfirmation = () => {
    if (deleteRowData) {
      setLoader(true);
      if (!deleteRowData.id) {
        setViewPR((prevData) =>
          ensure11Rows(
            prevData.filter((row) => row.tempId !== deleteRowData.tempId)
          )
        );
        toast.success(PRODUCT_DELETE_SUCCESS);
      }
      setLoader(false);
      setDeleteModal(false);
    }
  };
  const fetchStock = async (Id: number) => {
    setLoader(true);
    try {
      const res = await listOfStock(Id);
      if (checkStatusCodeSuccess(res?.statusCode)) {
        return res?.data?.quantity || 0;
      } else {
        return 0;
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message);
      return 0;
    } finally {
      setLoader(false);
    }
  };

  const updateRowData = (rowIndex: number, newData: any) => {
    setViewPR((prevData) => {
      const newArray = [...prevData];
      newArray[rowIndex] = newData;
      return ensure11Rows(newArray);
    });
  };

  useEffect(() => {
    if (!id && (!viewPR || viewPR.length === 0)) {
      setViewPR(ensure11Rows([]));
    }
  }, [id]);

  const OrderColumns = useMemo(
    () => [
      {
        headerName: SR_NO_LABEL,
        field: "srNo",
        valueGetter: "node.rowIndex + 1",
        flex: 1.5,
        minWidth: 150,
      },
      {
        headerName: ITEM_CODE_LABEL,
        field: "item_code",
        editable: true,
        flex: 1.5,
        minWidth: 180,
        sortable: true,
        cellEditor: (params: any) => {
          return (
            <ItemCodeEditor
              params={{
                ...params,
                itemCodes,
                context: {
                  handleProductSelect,
                  updateRowData,
                },
              }}
            />
          );
        },
        cellEditorPopup: true,
        valueFormatter: (params: { value: string }) => {
          return params.value || "";
        },
      },
      {
        headerName: MAKE_LABEL,
        field: "make",
        editable: !isReturnRoute,
        flex: 1.5,
        minWidth: 150,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: makeOption?.map((option: any) => option?.label),
          formatValue: (value: any) => {
            const selectedOption = makeOption?.find(
              (opt) => opt?.label === value
            );
            return selectedOption ? selectedOption?.label : value;
          },
        },
        valueFormatter: (params: { value: string }) => {
          if (!params.value) return "";
          return params.value.split("|")[0];
        },
        valueSetter: (params: any) => {
          const selectedValue = params.newValue;
          const selectedOption = makeOption?.find(
            (opt) => opt?.label === selectedValue
          );
          if (selectedOption) {
            params.data[MAKE_KEY] = selectedOption?.label;
            params.data.manufacturer_id = selectedOption.id;

            if (params.data.product_id) {
              fetchRate(params.data.product_id, selectedOption.id);
            }
            return true;
          }
          return false;
        },
      },
      {
        headerName: RATE_LABEL,
        field: "rate",
        editable: !isReturnRoute,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: (params: any) => {
          const productId = params.data?.product_id;
          const options = rateOptions[productId] || [];
          return {
            values: options.map((option: { label: any }) => option.label),
          };
        },
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) {
            return "";
          }
          return params.value.toString();
        },
        valueGetter: (params: any) => {
          return params.data?.rate;
        },
        valueSetter: (params: any) => {
          const productId = params.data?.product_id;
          if (!productId) return false;

          const options = rateOptions[productId] || [];
          const selectedValue = params.newValue;
          const selectedOption = options.find(
            (opt: any) => opt.label === selectedValue
          );

          if (!selectedOption) return false;

          const newRate = Number(selectedOption.value);

          const updatedData = {
            ...params.data,
            rate: newRate,
            product_variant_id: selectedOption.id,
          };

          params.data.rate = newRate;
          params.data.product_variant_id = selectedOption.id;

          params.api.refreshCells({
            rowNodes: [params.node],
            columns: ["rate"],
            force: true,
          });

          setViewPR((prevData) =>
            prevData.map((row, index) =>
              index === params.node.rowIndex
                ? {
                    ...row,
                    rate: newRate,
                    product_variant_id: selectedOption.id,
                  }
                : row
            )
          );

          fetchStock(selectedOption.id).then((stockValue) => {
            setViewPR((prevData) =>
              prevData.map((row, index) =>
                index === params.node.rowIndex
                  ? {
                      ...row,
                      stock: stockValue,
                      rate: newRate,
                      product_variant_id: selectedOption.id,
                    }
                  : row
              )
            );
          });

          countAmount(updatedData, params.node.rowIndex);
          return true;
        },
      },

      {
        headerName: STOCK_LABEL,
        field: "stock",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        valueParser: (params: any) => {
          const value = params.newValue;
          return value ? parseFloat(value) : 0;
        },
      },
      {
        headerName: QTY_LABEL,
        field: "required_quantity",
        editable: true,
        flex: 1.5,
        headerClass: "required",
        minWidth: 150,
        valueParser: (params: any) => {
          const value = params.newValue;
          return value ? parseFloat(value) : 0;
        },
        valueSetter: (params: any) => {
          const newValue = parseFloat(params.newValue);
          if (params.data.product_id || params.data.item_code) {
            if (!isNaN(newValue)) {
              if (newValue <= 0) {
                toast.error(QTY_GREATER_THAN_ZERO);
                return false;
              }
              params.data.required_quantity = newValue;
              countAmount(params.data, params.node.rowIndex);
              return true;
            }
            return false;
          }
          params.data.required_quantity = isNaN(newValue) ? "" : newValue;
          return true;
        },
      },
      {
        headerName: GST_LABEL,
        field: "gst",
        headerClass: isReturnRoute ? "" : "required",
        editable: !isReturnRoute,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: TaxType?.map((option) => option?.label),
        },
        valueSetter: (params: any) => {
          const newValue = params.newValue;
          const selectedOption = TaxType?.find(
            (opt) => opt?.label === newValue
          );
          if (selectedOption) {
            handleGSTSelection(newValue, params.node.rowIndex);
            return true;
          }
          return false;
        },
        valueGetter: (params: any) => {
          if (!params.data) return "";
          const selectedOption = TaxType?.find(
            (opt) => opt?.value === (params.data.gst ?? null)
          );
          return selectedOption ? selectedOption.label : "";
        },
        cellStyle: (
          params: any
        ): { backgroundColor?: string; cursor?: string } => {
          return params.node.rowIndex === 0 ? {} : { cursor: "not-allowed" };
        },
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: AMOUNT_LABEL,
        field: "amount",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        valueFormatter: (params: any) => {
          const value = parseFloat(params.value);
          return isNaN(value) ? "0.00" : value.toFixed(2);
        },
      },
      {
        headerName: ACTION_LABEL,
        filter: false,
        flex: 1.5,
        minWidth: 150,
        cellRenderer: (params: any) => (
          <span>
            <i
              className="mdi mdi-delete-outline fs-4 text-danger cursor-pointer"
              onClick={() => handleDeleteProduct(params.data)}
            ></i>
          </span>
        ),
      },
    ],
    [itemCodes, makeOption, rateOptions]
  );

  useEffect(() => {
    Array.isArray(formik.values.orderItem) &&
      formik.values.orderItem.forEach((order: Order, index: number) => {
        const result: any = products.find((item: any) => {
          return item.value === order.product_id;
        });
        setProductId((prevProductIds) => {
          if (index !== -1) {
            const updatedProductIds = [...prevProductIds];
            updatedProductIds[index] = Number(order.product_id);
            return updatedProductIds;
          } else {
            return [...prevProductIds, Number(order.product_id)];
          }
        });
        if (result) {
          formik.setFieldValue(
            `orderItem[${index}].availableQuantity`,
            result.quantity
          );
          if (productId[index] !== order.product_id) {
            formik.setFieldValue(`orderItem[${index}].rate`, result.rate);
          }
        } else {
          formik.setFieldValue(`orderItem[${index}].availableQuantity`, 0);
        }
        const amount: number = Number(order.quantity) * Number(order.rate);
        const tax: number = Number(order.tax?.split("_")[0]);
        const finalAmount: number = amount + (amount * tax) / 100;
        if (finalAmount) {
          formik.setFieldValue(`orderItem[${index}].final_amount`, finalAmount);
        } else {
          formik.setFieldValue(`orderItem[${index}].final_amount`, 0);
        }
        if (order.return_quantity && order.rate) {
          const refundAmount: number =
            Number(order.return_quantity) * Number(order.rate);
          const refundAmountWithTax: number =
            refundAmount + (refundAmount * tax) / 100;

          if (refundAmountWithTax) {
            formik.setFieldValue(
              `orderItem[${index}].refund_amount`,
              refundAmountWithTax
            );
          } else {
            formik.setFieldValue(`orderItem[${index}].refund_amount`, 0);
          }
        }
      });
    let total_cost = formik.values.orderItem?.reduce(
      (acc: any, item: any, index: any) => {
        return (acc += item.final_amount);
      },
      0
    );
    setInitialTotalCost(total_cost);
    formik.setFieldValue(TOTAL_PRICE_KEY, Number(total_cost));
  }, [formik.values.orderItem]);

  useEffect(() => {
    if (formik.isSubmitting && Object.keys(formik.errors).length > 0) {
      const firstErrorKey = Object.keys(formik.errors)[0];

      if (firstErrorKey) {
        const errorElement: HTMLElement | null = document.querySelector(
          `[name="${firstErrorKey}"], #${firstErrorKey}`
        );

        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          if (
            errorElement.tagName === "INPUT" ||
            errorElement.tagName === "SELECT"
          ) {
            errorElement.focus();
          } else {
            const selectControl = errorElement.closest(".select__control");
            if (selectControl) {
              selectControl.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              const selectInput = selectControl.querySelector("input, select");
              if (selectInput) {
                (selectInput as HTMLElement).focus();
              }
            }
          }
        }
      }
    }
  }, [formik.isSubmitting, formik.errors]);

  useEffect(() => {
    if (viewOrderData) {
      const transformedProducts = viewOrderData?.orderItem?.map((item: any) => {
        return {
          product_id: item.Product?.id,
          quantity: item.quantity,
          rate: item.rate,
          tax: item.tax || "",
          return_quantity: "",
          final_amount: item.final_amount,
        };
      });
      formik.setFieldValue("orderItem", transformedProducts);
      formik.setFieldValue("order_type", viewOrderData?.order_type);
      formik.setFieldValue("auth_draft_id", viewOrderData?.authUser?.id);
    }
  }, [viewOrderData]);

  useEffect(() => {
    if (id) {
      fetchOrder();
    }
    fetchProduct();
    fetchCustomer();
  }, []);

  useEffect(() => {
    if (viewOrderData) {
      const transformedProducts = viewOrderData?.orderItem?.map(
        (item: any) => ({
          product_id: item.Product?.id,
          item_code: item.Product?.product_code,
          make: item.manufacturer?.name,
          manufacturer_id: item.manufacturer?.id,
          product_variant_id: item.productVariant?.id,
          required_quantity: "",
          rate: item.rate,
          gst: item.tax,
          amount: item.final_amount,
          tax: item.tax || "",
          return_quantity: 0,
          final_amount: item.final_amount,
          stock: item.quantity,
        })
      );

      setViewPR(ensure11Rows(transformedProducts));

      formik.setFieldValue("orderItem", transformedProducts);
      formik.setFieldValue("order_type", viewOrderData?.order_type);
      formik.setFieldValue("auth_draft_id", viewOrderData?.authUser?.id);
      formik.setFieldValue("order_date", NormalDate(viewOrderData?.order_date));
      formik.setFieldValue(
        "expected_shipment_date",
        NormalDate(viewOrderData?.expected_shipment_date)
      );
      formik.setFieldValue("total_price", viewOrderData?.total_price);

      if (id) {
        formik.setFieldValue("return_date", NormalDate(String(new Date())));
      }
    }
  }, [viewOrderData]);

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4">
            {id ? RETURN_ORDER_LABEL : ADD_ORDER_LABEL}
          </span>
          <button className="btn btn-danger" onClick={() => navigate("/order")}>
            <i className="mdi mdi-arrow-left" />
          </button>
        </div>
        <div className="card word-wrap">
          <div className="card-body">
            <form onSubmit={formik.handleSubmit} className="mt-2 mb-4">
              <div className="row">
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseSelect
                    name="auth_draft_id"
                    label={CustomerNameLabel}
                    options={customerList}
                    value={formik.values.auth_draft_id}
                    placeholder={SelectPlaceHolder(CustomerNameLabel)}
                    handleChange={(field: string, value: any) => {
                      formik.setFieldValue(field, value);
                      handleCustomerChange(value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.auth_draft_id}
                    touched={formik.touched.auth_draft_id}
                    isDisabled={id}
                    required
                  />
                </div>
                {!id ? (
                  <>
                    <div className="col-md-3 col-sm-6 col-12">
                      <BaseInput
                        name="auth_id"
                        label={CustomerIdLabel}
                        placeholder={InputPlaceHolder(CustomerIdLabel)}
                        value={formik.values.auth_id}
                        onChange={formik.handleChange}
                        handleBlur={formik.handleBlur}
                        error={formik.errors.auth_id}
                        touched={formik.touched.auth_id}
                        disabled
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {" "}
                    <div className="col-md-3 col-sm-6 col-12">
                      <BaseInput
                        name="return_date"
                        type="date"
                        label={RETURN_DATE}
                        value={formik.values.return_date}
                        onChange={formik.handleChange}
                        handleBlur={formik.handleBlur}
                        error={formik.errors.return_date}
                        touched={formik.touched.return_date}
                        required
                      />
                    </div>
                    <div className="col-md-3 col-sm-6 col-12 mb-3">
                      <BaseSelect
                        name="reason"
                        label={REASON_LABEL}
                        options={ReturnOrderReasons}
                        value={formik.values.reason}
                        placeholder={SelectPlaceHolder(REASON_LABEL)}
                        handleChange={(field: any, value: any) => {
                          formik.setFieldValue(field, value);
                        }}
                        handleBlur={formik.handleBlur}
                        error={formik.errors.reason}
                        touched={formik.touched.reason}
                        required
                      />
                    </div>
                  </>
                )}
                {!id && (
                  <>
                    <div className="col-md-3 col-sm-6 col-12">
                      <BaseInput
                        name="order_date"
                        type="date"
                        label={OrderDateLabel}
                        value={formik.values.order_date}
                        onChange={formik.handleChange}
                        handleBlur={formik.handleBlur}
                        error={formik.errors.order_date}
                        touched={formik.touched.order_date}
                        required
                      />
                    </div>
                    <div className="col-md-3 col-sm-6 col-12">
                      <BaseInput
                        name="expected_shipment_date"
                        label={SHIPMENT_DATE_LABEL}
                        type="date"
                        value={formik.values.expected_shipment_date}
                        onChange={formik.handleChange}
                        handleBlur={formik.handleBlur}
                        error={formik.errors.expected_shipment_date}
                        touched={formik.touched.expected_shipment_date}
                        required
                      />
                    </div>
                  </>
                )}
              </div>

              {!id && (
                <div className="col-12 mb-2 mt-3 d-flex justify-content-end">
                  <BaseButton
                    type="button"
                    className="btn btn-info"
                    onClick={handleAddProduct}
                    children={AddProductLabel}
                  />
                </div>
              )}

              <div className="table-css">
                <div className="ag-theme-quartz">
                  <AgGridReact
                    ref={gridRef}
                    rowData={viewPR}
                    columnDefs={OrderColumns}
                    suppressRowClickSelection={true}
                    defaultColDef={{
                      sortable: true,
                      resizable: true,
                      flex: 1,
                      tooltipComponentParams: {
                        color: "blue",
                      },
                    }}
                    onCellKeyDown={handleCellKeyDown}
                    domLayout="normal"
                    getRowHeight={() => 35}
                    overlayNoRowsTemplate={notFound.dataNotFound}
                  />
                </div>
              </div>

              <div className="row mt-3 d-flex justify-content-end align-items-end border-top pt-3">
                {!id && (
                  <div className="col-md-2 col-sm-6 col-12">
                    <BaseInput
                      name="total_price"
                      label={id ? "Return Amount" : TotalCostLabel}
                      type="number"
                      prepend={<i className="mdi mdi-currency-rupee"></i>}
                      value={totalAmount}
                      onChange={formik.handleChange}
                      handleBlur={formik.handleBlur}
                      error={formik.errors.total_price}
                      touched={formik.touched.total_price}
                      disabled
                      required
                    />
                  </div>
                )}
                <div className="col-auto d-flex justify-content-end gap-2 text-end mt-2">
                  <BaseButton
                    type="submit"
                    label={id ? RETURN_LABEL : submitLabel}
                    color="success"
                    loader={btnLoader}
                    disable={btnLoader}
                  />
                  <BaseButton
                    type="button"
                    label={cancelLabel}
                    onClick={() => navigate("/order")}
                    color="danger"
                  />
                </div>
              </div>
              <DeleteModal
                show={deleteModal}
                onCloseClick={closeDeleteModal}
                onDeleteClick={handleDeleteConfirmation}
                btnLoader={btnLoader}
              />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddOrder;
